/**
 * Single layer section renderer per Atlas Day 2 visual-layout commitment
 * C.1 (layer-stack within sub-region inspect view).
 *
 * Renders a single scope §6 layer's inspect content with consistent
 * heading + body framing. Caller passes layer number (4-11 excluding 10
 * — Layer 10 has its own dedicated ComposabilityReservedLayer component
 * for reserved framing) + descriptive title + content body.
 *
 * Style: bg-surface-1 + border + p-4. Subdued numbered prefix on heading
 * (e.g., "L4. Math vs judgment") to teach the layer numbering scheme
 * referenced in scope §6 + ReadingThisMap.
 *
 * Deeper-drill content (Layer 4 math expansion, Layer 7 full trace history,
 * Layer 9 residue count enumeration per scope §4.7) lives inside this
 * section as inline expandables or modal triggers per caller-controlled
 * children rendering.
 */

import type { JSX, ReactNode } from 'react';

export interface LayerSectionProps {
  /** Layer number (4-11; skip 10 — use ComposabilityReservedLayer for L10) */
  layer: number;
  /** Layer title (e.g., "Math vs judgment", "Trace") */
  title: string;
  children: ReactNode;
}

export default function LayerSection({ layer, title, children }: LayerSectionProps): JSX.Element {
  return (
    <section
      data-inspect-layer={layer}
      className="bg-surface-1 border border-border-subtle rounded-lg p-4"
    >
      <h2 className="text-sm font-semibold text-ink-bright mb-2">
        <span className="text-ink-muted font-mono mr-2">L{layer}.</span>
        {title}
      </h2>
      <div className="text-sm text-ink-secondary space-y-2">{children}</div>
    </section>
  );
}
